import React, {useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import {withMemo} from 'helpers/wrapper';
import StyledCollectionOnboarding
  from 'components/organisms/Onboardings/CollectionOnboarding/CollectionOnboarding.styles';
import TaskAlt from '@mui/icons-material/TaskAlt';
import utils from 'helpers/utils';
import Badge from 'components/atoms/Badges/Badge/Badge';
import Onboarding from 'components/organisms/Onboardings/Onboarding/Onboarding';
import ActionFab from 'components/molecules/Fabs/ActionFab/ActionFab';
import {Slide} from '@mui/material';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import {useAuthClient} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import constants from 'helpers/constants';
import {useCollectionStatStatus} from 'services/collection/stat/stat.utils';
import CircularProgress from 'components/atoms/Progress/CircularProgress/CircularProgress';
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import ProgressTooltip from 'components/molecules/Tooltips/ProgressTooltip/ProgressTooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import AutoAwesome from '@mui/icons-material/AutoAwesome';

const CollectionOnboarding = withMemo(React.forwardRef((props, ref) => {
  const {
    collection,
    showEmpty,
    isLoading,
    ...innerProps
  } = useComponentProps(props, 'CollectionOnboarding');

  const popperRef = useRef(null);
  const profileProvider = useProfile();
  const tableProvider = useTable();
  const fieldData = profileProvider.fieldData;

  const isSuggestion = Boolean(tableProvider?.view?.activeViews?.find((v) => v.name === 'suggestions'));
  const isRejection = Boolean(tableProvider?.view?.activeViews?.find((v) => v.name === 'rejections'));

  const client = useAuthClient();
  const hasAutoTags = Boolean((client?.props?.autotagPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.autotagPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
  const hasEnrichment = Boolean((client?.props?.enrichmentPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.enrichmentPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((client?.props?.companyInfoPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.companyInfoPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((client?.props?.creditSafePeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.creditSafePeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((client?.props?.ventureIqPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.ventureIqPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((client?.props?.fileUploadPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.fileUploadPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
  const hasAutoLookup = Boolean((client?.props?.autoLookupPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.autoLookupPeriod ?? constants.services.periods.never) !== constants.services.periods.never);

  const [statusBreakdown] = useCollectionStatStatus(
    !isLoading ? collection?.collectionId : null
  );

  const status = useMemo(() => {
    const status = {
      ...statusBreakdown,
      percentages: statusBreakdown ? []
        .concat(((collection?.tagGroups ?? []).filter((tg) => utils.isDefined(tg.autoTagType) && tg.autoTagType !== constants.collection.autoTagTypes.noAutoAssign).length > 0) ? [{
          label: 'Auto tags',
          value: Math.floor(+statusBreakdown.tags.inside.total > 0 ? statusBreakdown.tags.inside.finished / statusBreakdown.tags.inside.total * 100 : 100),
          msg: (!collection?.isActive || !hasAutoTags) ? 'inactive' : null,
          eta: ((statusBreakdown.tags.inside.total - statusBreakdown.tags.inside.finished) * constants.timing.eta.question.level2)
        }] : [])
        .concat(((fieldData?.customFields ?? []).filter((f) => f.autoLookup).length > 0) ? [{
          label: 'Auto lookup',
          value: Math.floor(+statusBreakdown.lookup.total > 0 ? statusBreakdown.lookup.finished / statusBreakdown.lookup.total * 100 : 100),
          msg: (!collection?.isActive || !hasAutoLookup) ? 'inactive' : null,
          eta: ((statusBreakdown.lookup.total - statusBreakdown.lookup.finished) * constants.timing.eta.lookup)
        }] : [])
        .concat(hasEnrichment ? [{
          label: 'Enrichment',
          value: Math.floor(+statusBreakdown.enrichment.total > 0 ? statusBreakdown.enrichment.finished / statusBreakdown.enrichment.total * 100 : 100),
          msg: (!collection?.isActive || !hasEnrichment) ? 'inactive' : null,
          eta: ((statusBreakdown.enrichment.total - statusBreakdown.enrichment.finished) * constants.timing.eta.enrichment)
        }] : [])
        .concat((collection?.projectSources?.length > 0) ? [{
          label: 'Suggestions',
          value: Math.floor((
            (+statusBreakdown.sources.total > 0 ? statusBreakdown.sources.finished / statusBreakdown.sources.total * 100 : 100) +
            (+statusBreakdown.suggestions.total > 0 ? statusBreakdown.suggestions.finished / statusBreakdown.suggestions.total * 100 : 100) +
            (+statusBreakdown.tags.outside.total > 0 ? statusBreakdown.tags.outside.finished / statusBreakdown.tags.outside.total * 100 : 100)
          ) / 3),
          msg: !collection?.isActive ? 'inactive' : null,
          eta: ((statusBreakdown.sources.total - statusBreakdown.sources.finished) * constants.timing.eta.source) +
            ((statusBreakdown.suggestions.total - statusBreakdown.suggestions.finished) * constants.timing.eta.question.level1) +
            ((statusBreakdown.tags.outside.total - statusBreakdown.tags.outside.finished) * constants.timing.eta.question.level2)
        }] : []) : []
    };

    status.processing = status.percentages.find((p) => +p.value < 100);
    status.progress = Math.floor(status.percentages.reduce((s, p) => s + p.value, 0) / status.percentages.length);

    return status;
  }, [statusBreakdown, hasAutoTags, hasAutoLookup, hasEnrichment, collection?.projectSources, collection?.tagGroups, collection?.isActive, fieldData?.customFields]);

  const openProfileEvent = useEffectEvent(profileProvider.openProfile);
  const toggleAssistantEvent = useEffectEvent(tableProvider.toggleAssistant);
  const [steps, todo] = useMemo(() => {
    let steps = [];
    if (collection) {
      steps = steps.concat([
        {
          label: 'Setup the scope',
          description: 'What are you looking for? Choose a description, topics and many more',
          completed: collection?.description?.length > 0 || collection?.projectTopics?.length > 0,
          actions: [
            {
              label: 'Assistant',
              icon: AutoAwesome,
              onClick: (e) => {
                popperRef.current?.close?.(e);
                toggleAssistantEvent?.();
              }
            },
            {
              label: !(collection?.description?.length > 0 || collection?.projectTopics?.length > 0) ? 'Add scope' : 'Show scope',
              onClick: (e) => {
                popperRef.current?.close?.(e);
                openProfileEvent?.({
                  activeSection: 'basics',
                  activeCard: !(collection?.description?.length > 0 || collection?.projectTopics?.length > 0) ? 'scope' : null
                });
              }
            }
          ]
        },
        {
          label: 'Add fields',
          description: `Add custom fields and let ${constants.basics.shortName} lookup the value for you`,
          completed: +collection?.customFieldCount > 0,
          actions: [
            {
              label: 'Assistant',
              icon: AutoAwesome,
              onClick: (e) => {
                popperRef.current?.close?.(e);
                toggleAssistantEvent?.();
              }
            },
            {
              label: !(+collection?.customFieldCount > 0) ? 'Add field' : 'Show fields',
              onClick: (e) => {
                popperRef.current?.close?.(e);
                openProfileEvent?.({
                  activeSection: 'fields',
                  onboarding: !(+collection?.customFieldCount > 0)
                });
              }
            }
          ]
        },
        {
          label: 'Add categories',
          description: `Organise your collection with categories and let ${constants.basics.shortName} auto-assign them for you`,
          completed: collection?.tagGroups?.length > 0,
          actions: [
            {
              label: 'Assistant',
              icon: AutoAwesome,
              onClick: (e) => {
                popperRef.current?.close?.(e);
                toggleAssistantEvent?.();
              }
            },
            {
              label: !(collection?.tagGroups?.length > 0) ? 'Add category' : 'Show categories',
              onClick: (e) => {
                popperRef.current?.close?.(e);
                openProfileEvent?.({
                  activeSection: 'categories',
                  onboarding: !(collection?.tagGroups?.length > 0)
                });
              }
            }
          ]
        },
        {
          label: 'Add sources',
          description: `By adding sources and (optional) filters you can let ${constants.basics.shortName} search companies for you`,
          completed: collection?.projectSources?.length > 0,
          actions: [
            {
              label: 'Assistant',
              icon: AutoAwesome,
              onClick: (e) => {
                popperRef.current?.close?.(e);
                toggleAssistantEvent?.();
              }
            },
            {
              label: !(collection?.projectSources?.length > 0) ? 'Add source' : 'Show sources',
              onClick: (e) => {
                popperRef.current?.close?.(e);
                openProfileEvent?.({
                  activeSection: 'sources',
                  onboarding: !(collection?.projectSources?.length > 0)
                });
              }
            }
          ]
        },
        {
          label: 'Enable services',
          description: 'Turn on services like \'Auto tags\', \'Auto lookup\' or \'Enrichment\' and many more',
          completed: constants.data.services
            .filter((service) => service.enabled)
            .filter((service) => {
              if (service.value === constants.services.types.autotags) {
                return Boolean((client?.props?.autotagPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
                  Boolean((collection?.autotagPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
              } else if (service.value === constants.services.types.autoLookup) {
                return Boolean((client?.props?.autoLookupPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
                  Boolean((collection?.autoLookupPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
              } else if (service.value === constants.services.types.linkedinEnrichment) {
                return Boolean((client?.props?.enrichmentPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
                  Boolean((collection?.enrichmentPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
              } else if (service.value === constants.services.types.companyInfoEnrichment) {
                return Boolean((client?.props?.companyInfoPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
                  Boolean((collection?.companyInfoPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
              } else if (service.value === constants.services.types.creditSafeEnrichment) {
                return Boolean((client?.props?.creditSafePeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
                  Boolean((collection?.creditSafePeriod ?? constants.services.periods.never) !== constants.services.periods.never);
              } else if (service.value === constants.services.types.ventureIqEnrichment) {
                return Boolean((client?.props?.ventureIqPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
                  Boolean((collection?.ventureIqPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
              } else if (service.value === constants.services.types.fileUploadEnrichment) {
                return Boolean((client?.props?.fileUploadPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
                  Boolean((collection?.fileUploadPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
              } else {
                return false
              }
            }).length > 0,
          actions: [
            {
              label: 'Assistant',
              icon: AutoAwesome,
              onClick: (e) => {
                popperRef.current?.close?.(e);
                toggleAssistantEvent?.();
              }
            },
            {
              label: 'Show services',
              onClick: (e) => {
                popperRef.current?.close?.(e);
                openProfileEvent?.({activeSection: 'services'});
              }
            }
          ]
        }
      ]);
    }

    return [steps, steps.filter((s) => !s.completed).length];
  }, [collection, client, openProfileEvent, toggleAssistantEvent]);

  const mdDown = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const onboarding = useMemo(() => {
    const autoOpen = !isSuggestion && !isRejection &&
      collection?.newInCollection?.[constants.collection.scopes.inside]?.total === 0;

    const renderTooltip = () => {
      return !status.processing ? 'Setup your collection' : <ProgressTooltip progress={status.percentages} />
    }

    const renderIcon = () => {
      return !status.processing ? TaskAlt :
        <Tooltip title={renderTooltip()}
                 placement="left"
                 enterDelay={0} leaveDelay={0}>
          <CircularProgress className="CollectionOnboarding-progress"
                            showPercentage={true}
                            value={status.progress}
                            color="white"
                            variant="determinate"
                            TypographyProps={{
                              variant: 'caption',
                              color: 'white'
                            }}/>
        </Tooltip>
    }

    return {
      tooltip: renderTooltip(),
      icon: renderIcon(),
      color: collection?.isActive ? 'primary' : 'warning',
      auth: utils.createAuth({attribute: 'collection.update', meta: {collection}}),
      badge: todo > 0 ? <Badge badgeContent={todo} color="error"/> : null,
      popper: <Onboarding title="How to setup your collection"
                          steps={steps}/>,
      ActionFabProps: {
        autoOpen: (todo === steps.length && autoOpen),
        TooltipProps: {
          placement: 'left',
          ...(status.processing ? {enterDelay: 0} : {})
        },
        DropDownPopperProps: {
          ref: popperRef,
          autoFocus: false,
          ContextPopperProps: {
            placement: 'top-end',
            outlined: false,
            reverseShadows: false,
            fixed: true,
            transparent: true,
            density: 'densest',
            radius: 'roundest',
            offset: [
              0,
              -utils.rem2Pixel(innerProps.theme.layout(mdDown ? '3sp' : '4sp'), innerProps.theme.typography.htmlFontSize)
            ],
            TransitionComponent: Slide,
            TransitionProps: {
              direction: 'left'
            }
          }
        }
      }
    }
  }, [isSuggestion, isRejection, collection, todo, steps,
    status.processing, status.percentages, status.progress, innerProps.theme, mdDown]);

  if (!isLoading && (todo > 0 || showEmpty)) {
    return <StyledCollectionOnboarding ref={ref} {...innerProps}>
      <ActionFab action={onboarding} isLoading={isLoading}/>
    </StyledCollectionOnboarding>
  }
}));

CollectionOnboarding.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  collection: PropTypes.object,
  showEmpty: PropTypes.bool,
  isLoading: PropTypes.bool
};

export default CollectionOnboarding;
